import { useLayoutEffect, useState } from "react";

export interface CoachmarkTargetRect {
  borderRadius: string;
  bottom: number;
  height: number;
  left: number;
  right: number;
  top: number;
  viewportHeight: number;
  viewportWidth: number;
  width: number;
}

export function findCoachmarkTarget(target: number | string) {
  const targetValue = String(target);
  return Array.from(
    document.querySelectorAll<HTMLElement>("[data-coachmark-target]"),
  ).find((element) => element.dataset.coachmarkTarget === targetValue) ?? null;
}

function isSameRect(
  current: CoachmarkTargetRect | null,
  next: CoachmarkTargetRect,
) {
  if (!current || current.borderRadius !== next.borderRadius) return false;

  return (
    Math.abs(current.bottom - next.bottom) < 0.5 &&
    Math.abs(current.height - next.height) < 0.5 &&
    Math.abs(current.left - next.left) < 0.5 &&
    Math.abs(current.right - next.right) < 0.5 &&
    Math.abs(current.top - next.top) < 0.5 &&
    Math.abs(current.viewportHeight - next.viewportHeight) < 0.5 &&
    Math.abs(current.viewportWidth - next.viewportWidth) < 0.5 &&
    Math.abs(current.width - next.width) < 0.5
  );
}

export function useCoachmarkTargetRect(
  target: number | string | null | undefined,
  active: boolean,
) {
  const [targetRect, setTargetRect] = useState<CoachmarkTargetRect | null>(null);

  useLayoutEffect(() => {
    if (!active || target == null) {
      setTargetRect(null);
      return;
    }

    let animationFrame = 0;
    let resizeObserver: ResizeObserver | null = null;
    let mutationObserver: MutationObserver | null = null;
    let targetElement: HTMLElement | null = null;

    const measure = () => {
      if (!targetElement?.isConnected) {
        resizeObserver?.disconnect();
        targetElement = findCoachmarkTarget(target);
        if (targetElement) {
          mutationObserver?.disconnect();
          mutationObserver = null;
          resizeObserver = new ResizeObserver(measure);
          resizeObserver.observe(targetElement);
        }
      }

      if (!targetElement) return;

      const rect = targetElement.getBoundingClientRect();
      const nextRect: CoachmarkTargetRect = {
        borderRadius: window.getComputedStyle(targetElement).borderRadius,
        bottom: rect.bottom,
        height: rect.height,
        left: rect.left,
        right: rect.right,
        top: rect.top,
        viewportHeight: window.innerHeight,
        viewportWidth: window.innerWidth,
        width: rect.width,
      };

      setTargetRect((current) => (isSameRect(current, nextRect) ? current : nextRect));
    };

    const scheduleMeasure = () => {
      cancelAnimationFrame(animationFrame);
      animationFrame = requestAnimationFrame(measure);
    };

    targetElement = findCoachmarkTarget(target);
    if (targetElement) {
      resizeObserver = new ResizeObserver(scheduleMeasure);
      resizeObserver.observe(targetElement);
    } else {
      mutationObserver = new MutationObserver(scheduleMeasure);
      mutationObserver.observe(document.body, { childList: true, subtree: true });
    }

    scheduleMeasure();
    window.addEventListener("resize", scheduleMeasure);
    window.addEventListener("scroll", scheduleMeasure, true);

    return () => {
      cancelAnimationFrame(animationFrame);
      mutationObserver?.disconnect();
      resizeObserver?.disconnect();
      window.removeEventListener("resize", scheduleMeasure);
      window.removeEventListener("scroll", scheduleMeasure, true);
    };
  }, [active, target]);

  return targetRect;
}
